import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { useLocale } from "../i18n/LocaleContext";

type NetworkNode = {
  city: string;
  coordinates: [number, number];
  business: string;
  description: string;
  focus: string[];
};

type NetworkContent = {
  eyebrow: string;
  title: string;
  body: string;
  activeEyebrow: string;
  structureLabel: string;
  nodes: NetworkNode[];
};

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const nodeVisuals: Record<string, string> = {
  Incheon: "/official-brands/roundlab.jpg",
  "Ho Chi Minh City": "/official-brands/ilso-share.jpg",
  Bangkok: "/brand-showcase/hero-products-source.jpg",
  Jakarta: "/official-brands/ilso-share.jpg",
  Yangon: "/brand-showcase/showcase-wide.jpg",
  "Phnom Penh": "/brand-showcase/hero-products-source.jpg",
  Almaty: "/brand-showcase/showcase-wide.jpg",
  "仁川": "/official-brands/roundlab.jpg",
  "胡志明市": "/official-brands/ilso-share.jpg",
  "曼谷": "/brand-showcase/hero-products-source.jpg",
  "雅加达": "/official-brands/ilso-share.jpg",
  "仰光": "/brand-showcase/showcase-wide.jpg",
  "金边": "/brand-showcase/hero-products-source.jpg",
  "阿拉木图": "/brand-showcase/showcase-wide.jpg",
};

export function GlobalNetwork() {
  const { locale } = useLocale();
  const isChinese = locale === "zh" || locale === "vi";

  const content: Record<string, NetworkContent> = {
    en: {
      eyebrow: "Regional Structure",
      title: "A Korea-based structure built for regional execution.",
      body: "Headquarters in Korea, legal subsidiaries in Vietnam, Thailand, and Indonesia, with representative offices across key regional markets.",
      activeEyebrow: "Current Location",
      structureLabel: "Corporate Structure",
      nodes: [
        { city: "Incheon", coordinates: [126.7052, 37.4563], business: "Registered Headquarters", description: "Korean legal headquarters supporting brand partnerships, supply-chain coordination, and corporate management.", focus: ["Legal Entity", "Supply Chain", "Brand Partnerships"] },
        { city: "Ho Chi Minh City", coordinates: [106.6297, 10.8231], business: "Legal Subsidiary", description: "Vietnam legal entity supporting regional business development, channel partnerships, and market execution.", focus: ["Vietnam", "Business Development", "Channel Execution"] },
        { city: "Bangkok", coordinates: [100.5018, 13.7563], business: "Legal Subsidiary", description: "Thailand legal entity focused on distributor development and structured local market expansion.", focus: ["Thailand", "Distribution", "Market Growth"] },
        { city: "Jakarta", coordinates: [106.8456, -6.2088], business: "Legal Subsidiary", description: "Indonesia legal entity supporting localized commercial execution and long-term retail growth.", focus: ["Indonesia", "Retail Access", "Local Operations"] },
        { city: "Yangon", coordinates: [96.1951, 16.8661], business: "Representative Office", description: "Representative office supporting market coordination and partner communication in Myanmar.", focus: ["Myanmar", "Coordination", "Partner Support"] },
        { city: "Phnom Penh", coordinates: [104.9282, 11.5564], business: "Representative Office", description: "Representative office supporting Cambodia market observation and commercial communication.", focus: ["Cambodia", "Market Watch", "Commercial Liaison"] },
        { city: "Almaty", coordinates: [76.886, 43.238], business: "Representative Office", description: "Representative office extending QIANDU's network reach toward Central Asia through Kazakhstan.", focus: ["Kazakhstan", "Regional Reach", "Market Presence"] },
      ],
    },
    zh: {
      eyebrow: "区域组织结构",
      title: "以韩国为总部，服务区域市场的国际布局。",
      body: "韩国总部统筹品牌与供应链，在越南、泰国、印尼设有法人分公司，并覆盖多个区域办事处。",
      activeEyebrow: "当前地区",
      structureLabel: "组织结构",
      nodes: [
        { city: "仁川", coordinates: [126.7052, 37.4563], business: "注册总部", description: "韩国法定总部与登记经营场所，支持品牌合作、供应链协调与公司管理。", focus: ["法人主体", "供应链协同", "品牌合作"] },
        { city: "胡志明市", coordinates: [106.6297, 10.8231], business: "法人分公司", description: "越南法人分公司，负责区域业务拓展、渠道合作与市场执行。", focus: ["越南", "业务拓展", "渠道执行"] },
        { city: "曼谷", coordinates: [100.5018, 13.7563], business: "法人分公司", description: "泰国法人分公司，负责分销体系发展与本地市场拓展。", focus: ["泰国", "分销发展", "市场增长"] },
        { city: "雅加达", coordinates: [106.8456, -6.2088], business: "法人分公司", description: "印尼法人分公司，支撑本地化商业执行与长期零售增长。", focus: ["印尼", "零售拓展", "本地运营"] },
        { city: "仰光", coordinates: [96.1951, 16.8661], business: "办事处", description: "缅甸办事处，负责区域市场协调与合作方沟通。", focus: ["缅甸", "市场协调", "伙伴支持"] },
        { city: "金边", coordinates: [104.9282, 11.5564], business: "办事处", description: "柬埔寨办事处，负责市场观察与商业联络。", focus: ["柬埔寨", "市场观察", "商务联络"] },
        { city: "阿拉木图", coordinates: [76.886, 43.238], business: "办事处", description: "哈萨克斯坦办事处，用于延伸中亚区域市场覆盖。", focus: ["哈萨克斯坦", "区域覆盖", "市场存在"] },
      ],
    },
    ko: {
      eyebrow: "지역 운영 구조",
      title: "한국 본사를 중심으로 운영되는 지역 실행 네트워크.",
      body: "한국 본사와 베트남, 태국, 인도네시아 법인, 그리고 주요 지역 사무소로 시장 실행을 지원합니다.",
      activeEyebrow: "현재 거점",
      structureLabel: "조직 구조",
      nodes: [
        { city: "Incheon", coordinates: [126.7052, 37.4563], business: "Registered Headquarters", description: "인천 등록 본점에서 브랜드 파트너십, 공급망 협업 및 법인 운영을 지원합니다.", focus: ["법인", "공급망", "브랜드 파트너십"] },
        { city: "Ho Chi Minh City", coordinates: [106.6297, 10.8231], business: "Legal Subsidiary", description: "베트남 법인은 지역 사업 개발, 채널 협력, 시장 실행을 담당합니다.", focus: ["베트남", "사업 개발", "채널 실행"] },
        { city: "Bangkok", coordinates: [100.5018, 13.7563], business: "Legal Subsidiary", description: "태국 법인은 유통 네트워크 확대와 현지 시장 확장을 담당합니다.", focus: ["태국", "유통", "시장 성장"] },
        { city: "Jakarta", coordinates: [106.8456, -6.2088], business: "Legal Subsidiary", description: "인도네시아 법인은 현지 상업 실행과 장기 리테일 성장 기반을 지원합니다.", focus: ["인도네시아", "리테일", "현지 운영"] },
        { city: "Yangon", coordinates: [96.1951, 16.8661], business: "Representative Office", description: "미얀마 사무소는 시장 조정과 파트너 커뮤니케이션을 지원합니다.", focus: ["미얀마", "조정", "파트너 지원"] },
        { city: "Phnom Penh", coordinates: [104.9282, 11.5564], business: "Representative Office", description: "캄보디아 사무소는 시장 관찰과 상업 커뮤니케이션을 담당합니다.", focus: ["캄보디아", "시장 관찰", "커뮤니케이션"] },
        { city: "Almaty", coordinates: [76.886, 43.238], business: "Representative Office", description: "카자흐스탄 사무소는 중앙아시아로의 네트워크 확장을 보여줍니다.", focus: ["카자흐스탄", "지역 확장", "시장 존재감"] },
      ],
    },
    vi: {
      eyebrow: "Cấu trúc khu vực",
      title: "Mạng lưới vận hành lấy Hàn Quốc làm trung tâm.",
      body: "Trụ sở tại Hàn Quốc, pháp nhân tại Việt Nam, Thái Lan, Indonesia và các văn phòng đại diện tại các thị trường trọng điểm.",
      activeEyebrow: "Điểm hiện tại",
      structureLabel: "Cấu trúc doanh nghiệp",
      nodes: [
        { city: "Incheon", coordinates: [126.7052, 37.4563], business: "Trụ sở đăng ký", description: "Trụ sở pháp lý tại Hàn Quốc hỗ trợ hợp tác thương hiệu, điều phối chuỗi cung ứng và quản lý doanh nghiệp.", focus: ["Pháp nhân", "Chuỗi cung ứng", "Hợp tác thương hiệu"] },
        { city: "Ho Chi Minh City", coordinates: [106.6297, 10.8231], business: "Pháp nhân", description: "Pháp nhân tại Việt Nam phụ trách phát triển kinh doanh khu vực, hợp tác kênh và triển khai thị trường.", focus: ["Việt Nam", "Kinh doanh", "Triển khai kênh"] },
        { city: "Bangkok", coordinates: [100.5018, 13.7563], business: "Pháp nhân", description: "Pháp nhân tại Thái Lan tập trung vào phát triển phân phối và tăng trưởng thị trường địa phương.", focus: ["Thái Lan", "Phân phối", "Tăng trưởng"] },
        { city: "Jakarta", coordinates: [106.8456, -6.2088], business: "Pháp nhân", description: "Pháp nhân tại Indonesia hỗ trợ vận hành thương mại bản địa và tăng trưởng bán lẻ dài hạn.", focus: ["Indonesia", "Bán lẻ", "Vận hành"] },
        { city: "Yangon", coordinates: [96.1951, 16.8661], business: "Văn phòng đại diện", description: "Văn phòng tại Myanmar hỗ trợ điều phối thị trường và giao tiếp với đối tác.", focus: ["Myanmar", "Điều phối", "Hỗ trợ đối tác"] },
        { city: "Phnom Penh", coordinates: [104.9282, 11.5564], business: "Văn phòng đại diện", description: "Văn phòng tại Campuchia hỗ trợ quan sát thị trường và liên lạc thương mại.", focus: ["Campuchia", "Quan sát thị trường", "Liên lạc"] },
        { city: "Almaty", coordinates: [76.886, 43.238], business: "Văn phòng đại diện", description: "Văn phòng tại Kazakhstan mở rộng sự hiện diện của QIANDU tới khu vực Trung Á.", focus: ["Kazakhstan", "Mở rộng khu vực", "Hiện diện"] },
      ],
    },
  };

  const current = content[locale] ?? content.en;
  const [selected, setSelected] = useState<string>(current.nodes[0]?.city ?? "");
  const activeNode = useMemo(
    () => current.nodes.find((node) => node.city === selected) ?? current.nodes[0],
    [current, selected]
  );

  useEffect(() => {
    setSelected(current.nodes[0]?.city ?? "");
  }, [current]);

  return (
    <section id="network" className="px-5 py-24 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-10 max-w-[24rem]">
          <p className="text-[0.72rem] uppercase tracking-[0.44em] text-aurora/78">{current.eyebrow}</p>
          <h2 className={`mt-4 leading-[1.04] text-[#1f2b31] ${
            isChinese
              ? "font-sans text-[2.05rem] font-medium tracking-[-0.01em] sm:text-[2.9rem]"
              : "font-display text-[2.3rem] font-semibold sm:text-[4rem]"
          }`}>
            {current.title}
          </h2>
          <p className="mt-5 max-w-[16rem] text-[0.88rem] leading-7 text-soft/54">{current.body}</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.28fr_0.72fr] lg:gap-7">
          <div className="rounded-[2.65rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.92),rgba(239,246,249,0.95))] p-3.5 shadow-[0_12px_24px_rgba(106,138,151,0.05)] backdrop-blur sm:p-5">
            <ComposableMap projection="geoMercator" projectionConfig={{ scale: 145, center: [95, 20] }} className="h-full w-full">
              <Geographies geography={geoUrl}>
                {({ geographies }: { geographies: Array<{ rsmKey: string }> }) =>
                  geographies.map((geo: { rsmKey: string }) => (
                    <Geography key={geo.rsmKey} geography={geo} fill="#eaf1f4" stroke="#c9dce4" strokeWidth={0.6} />
                  ))
                }
              </Geographies>

              {current.nodes.map((node) => (
                <Marker key={node.city} coordinates={node.coordinates}>
                  <g onClick={() => setSelected(node.city)} className="cursor-pointer">
                    <circle r={selected === node.city ? 7 : 5} fill="#7ea7b5" stroke="#ffffff" strokeWidth={1.2} />
                    <circle r={selected === node.city ? 16 : 12} fill="rgba(142,190,205,0.18)" stroke="rgba(126,167,181,0.24)" />
                  </g>
                </Marker>
              ))}
            </ComposableMap>
          </div>

          <motion.div
            key={activeNode.city}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-[2.65rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.92),rgba(239,246,249,0.95))] p-5 shadow-[0_12px_24px_rgba(106,138,151,0.05)] backdrop-blur sm:p-7"
          >
            <div className="mb-6 overflow-hidden rounded-[1.8rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
              <img
                src={nodeVisuals[activeNode.city] ?? "/brand-showcase/showcase-wide.jpg"}
                alt={activeNode.city}
                className="h-48 w-full object-contain p-3 sm:p-4"
              />
            </div>
            <p className="text-[9px] uppercase tracking-[0.38em] text-aurora/74">{current.activeEyebrow}</p>
            <h3 className="mt-4 font-display text-[1.8rem] text-[#1f2b31] sm:text-[2.75rem]">{activeNode.city}</h3>
            <div className="mt-5 inline-flex rounded-full bg-[#ecf6f9] px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-[#6c8b97]">
              {activeNode.business}
            </div>
            <p className="mt-5 max-w-[17rem] text-[0.88rem] leading-7 text-soft/56">{activeNode.description}</p>
            <div className="mt-7 text-[8px] uppercase tracking-[0.34em] text-soft/42">{current.structureLabel}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {activeNode.focus.map((item) => (
                <span key={item} className="rounded-full bg-[#f3f9fb] px-3 py-1 text-[8px] uppercase tracking-[0.28em] text-soft/58">
                  {item}
                </span>
              ))}
            </div>
            <div className="mt-7 grid gap-2 sm:grid-cols-2">
              {current.nodes.map((node) => (
                <button
                  key={node.city}
                  type="button"
                  onClick={() => setSelected(node.city)}
                  className={`flex items-center justify-between rounded-[1.25rem] px-4 py-3 text-left transition ${
                    node.city === activeNode.city
                      ? "bg-[rgba(255,255,255,0.74)] shadow-[0_8px_18px_rgba(106,138,151,0.05)]"
                      : "bg-transparent hover:bg-[rgba(255,255,255,0.52)]"
                  }`}
                >
                  <span className="pr-3 text-sm text-[#1f2b31]">{node.city}</span>
                  <span className="text-[8px] uppercase tracking-[0.22em] text-soft/48">
                    {node.business}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
